import { type Address, BITS, type Family } from "./parse";

export interface Block {
  family: Family;
  network: bigint;
  prefix: number;
}

export interface Hosts {
  first: bigint;
  last: bigint;
  usable: bigint;
}

// The address with its host bits taken off, which is the block it is in and the thing every row below is read from.
export function blockOf({ family, value }: Address, prefix: number): Block {
  return { family, network: value & maskFor(family, prefix), prefix };
}

export function maskFor(family: Family, prefix: number): bigint {
  return ((1n << BigInt(prefix)) - 1n) << BigInt(BITS[family] - prefix);
}

export function wildcardFor(family: Family, prefix: number): bigint {
  return (1n << BigInt(BITS[family] - prefix)) - 1n;
}

export function sizeOf({ family, prefix }: Block): bigint {
  return 1n << BigInt(BITS[family] - prefix);
}

export function lastOf(block: Block): bigint {
  return block.network + sizeOf(block) - 1n;
}

export function holds(block: Block, value: bigint): boolean {
  return value >= block.network && value <= lastOf(block);
}

export function holdsBlock(outer: Block, inner: Block): boolean {
  return inner.network >= outer.network && lastOf(inner) <= lastOf(outer);
}

// What a host may be given out of the block, which is all of it but for IPv4's network and broadcast addresses — and
// RFC 3021 hands both of those back at /31, a point-to-point link having no room to spend two addresses on either.
export function hostsOf(block: Block): Hosts {
  const size = sizeOf(block);
  const last = lastOf(block);
  if (block.family === "ipv6" || size <= 2n) return { first: block.network, last, usable: size };
  return { first: block.network + 1n, last: last - 1n, usable: size - 2n };
}

export function splitCount(block: Block, prefix: number): bigint {
  if (prefix <= block.prefix || prefix > BITS[block.family]) return 0n;
  return 1n << BigInt(prefix - block.prefix);
}

// A /48 splits into 65536 /64s, so the count is answered separately and only as many as asked for are ever built.
export function split(block: Block, prefix: number, limit: number): Block[] {
  const count = splitCount(block, prefix);
  const shown = count < BigInt(limit) ? count : BigInt(limit);
  const step = 1n << BigInt(BITS[block.family] - prefix);

  const blocks: Block[] = [];
  for (let index = 0n; index < shown; index++) {
    blocks.push({ family: block.family, network: block.network + index * step, prefix });
  }
  return blocks;
}

// Where in its block the address sits, which for IPv4 is what says whether it may be given to a host at all. RFC 3021
// again: a /31 is two hosts and neither of them is the network or the broadcast address.
export function roleOf({ family, value }: Address, block: Block): string {
  const size = sizeOf(block);
  if (size === 1n) return "A single address";
  if (family === "ipv4" && size === 2n) return "Usable host";
  if (value === block.network) return "Network address";
  if (family === "ipv4" && value === lastOf(block)) return "Broadcast address";
  return "Usable host";
}
